/**
 * 🚨 CRITICAL COMPONENT - NEW HOMEPAGE
 *
 * Primary user entry point. Handles grid display, search, filters, and modal routing.
 *
 * ⚠️ BEFORE MODIFYING:
 * 1. Read /docs/SYSTEMS/HOMEPAGE_SYSTEM.md
 * 2. Read /docs/ESSENTIAL/CRITICAL_SYSTEMS.md
 * 3. Check modal scroll coordination
 *
 * 🔒 CRITICAL RULES:
 * - NEVER change GRID_SCROLL_OFFSET (imported from uiConstants.js)
 * - ALWAYS coordinate with MODAL_MAX_HEIGHT + MODAL_SPACING
 * - NEVER remove scroll lock while modal is open
 * - ALWAYS use intelligentLoadingService for data caching
 *
 * 📍 CRITICAL SECTIONS:
 * - Lines ~550-580: Modal scroll positioning and lock
 * - Line ~557: Grid offset using GRID_SCROLL_OFFSET constant
 * - Lines ~440-476: Navigation handling and modal routing
 *
 * 🎯 MODAL COORDINATION:
 * - Modal height: MODAL_MAX_HEIGHT (610px from uiConstants.js)
 * - Grid offset: GRID_SCROLL_OFFSET (660px from uiConstants.js)
 * - Scroll lock: Always active when modal open
 *
 * Last reviewed: 2025-01-15
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import MaterialIcon from './MaterialIcon';
import SuperSimpleStickerCard from './SuperSimpleStickerCard';
import StickerModal from './StickerModal';
import StickerGrid from './StickerGrid';
import ParallaxComponent from './ParallaxComponent';
import TagFilter from './TagFilter';
import TagChip from './TagChip';
import HeroScene from './HeroScene';
import MainHeader from './MainHeader';
import MainNavigation from './MainNavigation';
import ClothedEyeLoader from './ClothedEyeLoader';
import ClothedEyeError from './ClothedEyeError';
import databaseService from '../services/databaseService';
import parallaxService from '../services/parallaxService';
import intelligentLoadingService from '../services/intelligentLoadingService';
import navigationService from '../services/navigationService';
import { getColorByIndex } from '../data/colors';
import { trackEtsyClick, trackStickerEngagement, trackSearchFilter } from '../services/analyticsService';
import { GRID_SCROLL_OFFSET } from '../config/uiConstants';
import logger from '../utils/logger.js';
import './NewHomePage.css';

// Debug flag to silence modal/grid logs unless explicitly enabled
const DEBUG_MODAL_FEED = false;

const NewHomePage = React.memo(({ isInitialAppLoad = true, hasLoadedData = false }) => {
  
  // State for listings and filtering (NO PERSISTENCE - fresh state every time)
  const [allListings, setAllListings] = useState([]); // All loaded listings
  const [listings, setListings] = useState([]); // Filtered listings
  const [loading, setLoading] = useState(true);
  
  // Track if data has been loaded to handle navigation scenarios
  const hasDataBeenLoadedRef = useRef(false);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [sortBy, setSortBy] = useState('sales');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [userGroupByCategory, setUserGroupByCategory] = useState(true); // User's actual preference
  // Modal state (must be declared before any effects that reference it)
  const [selectedListing, setSelectedListing] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageLoaded, setModalImageLoaded] = useState(false);
  
  // Tag filter state (NO PERSISTENCE)
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [isGridReversed, setIsGridReversed] = useState(false); // For tag filter grid reverse
  
  // Header visibility state for sticky navigation merge
  const [headerVisible, setHeaderVisible] = useState(true);
  
  // Keep refs in sync with state for modal handler access
  useEffect(() => {
    currentFilterTextRef.current = filterText;
  }, [filterText]);
  
  useEffect(() => {
    currentSelectedTagsRef.current = selectedTags;
  }, [selectedTags]);
  
  // Reset modal image loaded state whenever selected listing changes
  useEffect(() => {
    if (selectedListing) {
      setModalImageLoaded(false);
      logger.log('🔄 Selected listing changed - reset modal image loaded state');
    }
  }, [selectedListing?.etsyListingId, selectedListing?.listingId]);
  
  // Priority loading state - for deep links
  const [priorityListingId, setPriorityListingId] = useState(null);
  
  // Ref to control scrolling the grid into view
  const gridRef = useRef(null);
  // Track last-opened modal listing to avoid redundant state updates
  const lastModalListingIdRef = useRef(null);
  const didScrollForModalRef = useRef(false);
  const preModalScrollYRef = useRef(0);
  // Lock the minimum scroll position while modal is open so hero isn't revealed
  const minScrollLockRef = useRef(null);
  const scrollLockHandlerRef = useRef(null);
  // Save filter state before modal opens to restore on close
  const preModalFilterTextRef = useRef('');
  const preModalSelectedTagsRef = useRef([]);
  // Keep current filter state in refs for modal handler access (avoids closure issues)
  const currentFilterTextRef = useRef('');
  const currentSelectedTagsRef = useRef([]);

  // Tags to exclude from filtering (generic/common tags)
  const excludedTags = [
    'jeep', 'easter egg', 'sticker', 'decal', 'clothed eye', 'easter eggs', 
    'vehicle', 'windshield'
  ];
  
  // Auto-ungroup when filters are applied (but never re-group while modal is open)
  useEffect(() => {
    // If modal is open, do not modify grouping here to avoid race with modal logic
    if (isModalOpen) return;

    const hasFilters = filterText.trim() !== '' || selectedTags.length > 0;
    if (hasFilters) {
      // Filters are active - force ungrouping for better layout
      setGroupByCategory(false);
    } else {
      // No filters - restore user's grouping preference
      setGroupByCategory(userGroupByCategory);
    }
  }, [filterText, selectedTags, userGroupByCategory, isModalOpen]);

  // Debug: log grouping and modal state transitions (gated)
  useEffect(() => {
    if (DEBUG_MODAL_FEED) console.log('🧭 groupByCategory changed:', groupByCategory);
  }, [groupByCategory]);

  // Moved below isModalOpen declaration to avoid temporal dead zone

  useEffect(() => {
    if (DEBUG_MODAL_FEED) console.log('🧭 listings length changed:', listings.length, 'allListings:', allListings.length);
  }, [listings.length, allListings.length]);

  // (moved up)

  // Debug: now safe to reference isModalOpen after declaration
  useEffect(() => {
    if (DEBUG_MODAL_FEED) console.log('🧭 isModalOpen changed:', isModalOpen);
  }, [isModalOpen]);
  
  // Load tags from database once on component mount
  useEffect(() => {
    const loadTags = async () => {
      try {
        // Add cache-busting timestamp to ensure we get fresh data
        const tagData = await databaseService.getAllTags();
        
        // No filtering - just use all tags sorted by usage count (backend already sorts)
        setAllTags(tagData || []);

      } catch (error) {
        console.error('❌ Error loading tags:', error);
        setAllTags([]);
      }
    };
    
    loadTags();
  }, []); // Only run once on mount

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Loading progress state
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0, message: 'Initializing...' });
  


  // (moved up)

  // Ref to maintain search input focus
  const searchInputRef = useRef(null);
  const sortDropdownRef = useRef(null);

  // Background loading function for remaining pages
  const backgroundLoadingRef = useRef(null);
  
  const loadRemainingPages = async (initialPagination) => {
    // Cancel any existing background loading
    if (backgroundLoadingRef.current) {
      backgroundLoadingRef.current.cancelled = true;
    }
    
    // Create new background loading controller
    const loadingController = { cancelled: false };
    backgroundLoadingRef.current = loadingController;
    
    let currentPage = 2; // Start from page 2
    let hasMore = initialPagination.hasNext;
    
    while (hasMore && !loadingController.cancelled) {
      try {
        const result = await databaseService.getUnifiedStickers({
          page: currentPage,
          limit: 50,
          sortBy: 'sales', // Use default sort for background loading
          sortOrder: 'desc',
          publicVisible: true
        });
        
        // Check if loading was cancelled during the async call
        if (loadingController.cancelled) {

          break;
        }
        
        if (result.data && result.data.length > 0) {
          setAllListings(prev => [...prev, ...result.data]);
          setListings(prev => [...prev, ...result.data]);
          // Background loading debug reduced
        }
        
        hasMore = result.pagination?.hasNext || false;
        currentPage++;
        
        // Small delay to not overwhelm the system
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (error) {
        console.error(`❌ Error loading page ${currentPage}:`, error);
        break;
      }
    }
    
    if (!loadingController.cancelled) {

    }
  };

  // Category scene assignments state
  const [categorySceneAssignments, setCategorySceneAssignments] = useState({});
  const [categoryScenes, setCategoryScenes] = useState({});

  // Load category scene assignments
  useEffect(() => {
    const loadCategorySceneAssignments = async () => {
      try {
        const assignments = await parallaxService.getAllCategorySceneAssignments();
        const assignmentsMap = {};
        
        assignments.forEach(assignment => {
          assignmentsMap[assignment.category] = assignment.scene_name;
        });
        
        setCategorySceneAssignments(assignmentsMap);

      } catch (error) {
        console.error('❌ Error loading category scene assignments:', error);
      }
    };
    
    loadCategorySceneAssignments();
  }, []);

  // Parallax scene state
  const [homeSceneStickers, setHomeSceneStickers] = useState([]);
  const [currentHomeSceneName, setCurrentHomeSceneName] = useState('home-dino1');

  // Parallax callbacks
  const handleHomeSceneStickersUpdate = useCallback((newStickers) => {
    setHomeSceneStickers(newStickers);
  }, []);

  const handleHomeSceneNameUpdate = useCallback((sceneName) => {
    setCurrentHomeSceneName(sceneName || 'home-dino1');
  }, []);

  const handleStickerClick = useCallback(async (data) => {
    // Handle sticker clicks from parallax scene and hero scene

    
    // Handle both listingId (from parallax) and id (from hero scene)
    const listingId = data.listingId || data.id;
    
    // First try to find the listing in already loaded data
    let listing = allListings.find(item => item.etsyListingId === listingId);
    
    if (!listing) {
      // If not found in allListings, fetch it specifically from the database

      try {
        const unifiedData = await databaseService.getUnifiedStickerData(listingId);

        if (unifiedData && unifiedData.data) {
          listing = unifiedData.data;

        } else {
          logger.warn('⚠️ API returned null/empty data for listing:', listingId);
        }
      } catch (error) {
        console.error('❌ Error fetching listing data:', error);
      }
    }
    
    if (listing) {
      // Convert color index to color name for the modal
      let clickedColor = 'Black (Matte)'; // Default fallback
      if (data.colorIndex !== undefined && data.colorIndex !== null) {
        const colorData = getColorByIndex(data.colorIndex);
        if (colorData) {
          clickedColor = colorData.name;
        }
      }
      
      
      // Track hero card click for analytics
      trackStickerEngagement(
        listingId, 
        'hero_card_click', 
        listing.category, 
        data.size,
        listing.title
      );
      
      // Add the clicked size and color to the listing data for the modal
      const listingWithSizeAndColor = {
        ...listing,
        clickedSize: data.size, // Add the size that was clicked
        clickedColor: clickedColor // Add the actual color name that was clicked
      };
      
      // Use navigation service to open modal with URL routing
      navigationService.openModal(listingWithSizeAndColor);

    } else {
      console.error('❌ Listing not found for ID:', listingId);
    }
  }, [allListings]);

  const handleAdminToggle = useCallback((isOpen) => {
    // Admin panel toggled - not needed for homepage
  }, []);








  // Enhanced loading with navigation-aware caching using intelligentLoadingService
  useEffect(() => {
    const loadData = async () => {
      // Track if data has been loaded for navigation scenarios
      if (hasDataBeenLoadedRef.current) {
        logger.log('📡 Data already loaded, skipping reload for navigation');
        return;
      }
      
      // If we already have data in state, don't reload
      if (allListings.length > 0) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setLoadingProgress({ current: 0, total: 0, message: 'Checking cache...' });
        
        // Check cache first for instant display on navigation
        const cachedListings = intelligentLoadingService.getCachedData('homePageListings');
        
        if (cachedListings && Array.isArray(cachedListings) && cachedListings.length > 0) {
          // Instant display from cache
          logger.log('⚡ Using cached listings for instant display:', cachedListings.length, 'items');
          setAllListings(cachedListings);
          setListings(cachedListings);
          hasDataBeenLoadedRef.current = true;
          setLoading(false);

          // 🎯 Start grid loading after modal is ready (if deep link opened modal)
          intelligentLoadingService.startGridLoadingAfterModal(cachedListings);

          // Start background refresh for fresh data (non-blocking)
          intelligentLoadingService.refreshListingsInBackground();
          return;
        }
        
        // No cache - load fresh data with progress tracking
        setLoadingProgress({ current: 0, total: 0, message: 'Loading stickers...' });
        
        const freshListings = await intelligentLoadingService.loadHomepageListings({
          sortBy: 'sales',
          sortOrder: 'desc',
          publicVisible: true
        });
        
        if (freshListings && freshListings.length > 0) {
          setAllListings(freshListings);
          setListings(freshListings);
          hasDataBeenLoadedRef.current = true;

          // 🎯 Start grid loading after modal is ready (if deep link opened modal)
          intelligentLoadingService.startGridLoadingAfterModal(freshListings);

          setLoadingProgress({
            current: freshListings.length,
            total: freshListings.length,
            message: `Loaded ${freshListings.length} stickers`
          });
        } else {
          logger.warn('⚠️ No listings data received');
          setError('No stickers found');
        }
        
      } catch (error) {
        console.error('❌ Error loading listings:', error);
        setError(error.message);
        
        // Try fallback loading
        try {
          logger.log('🔄 Attempting fallback loading...');
          const fallbackResult = await databaseService.getAllUnifiedStickers({
            sortBy: 'sales',
            sortOrder: 'desc',
            publicVisible: true
          });
          
          if (fallbackResult.data && fallbackResult.data.length > 0) {
            logger.log('✅ Fallback loading succeeded:', fallbackResult.data.length, 'items');
            setAllListings(fallbackResult.data);
            setListings(fallbackResult.data);
            hasDataBeenLoadedRef.current = true;

            // 🎯 Start grid loading after modal is ready (if deep link opened modal)
            intelligentLoadingService.startGridLoadingAfterModal(fallbackResult.data);

            // Cache the fallback data
            intelligentLoadingService.persistCriticalData('homePageListings', fallbackResult.data);
          }
        } catch (fallbackError) {
          console.error('❌ Fallback loading also failed:', fallbackError);
        }
      } finally {
        setLoading(false);

        // After data loads, check for deep links
        const modalStateFromURL = await navigationService.initializeFromURL(databaseService);
        if (modalStateFromURL) {
          const priorityId = modalStateFromURL.data?.etsyListingId || modalStateFromURL.data?.listingId;
          if (priorityId) {
            setPriorityListingId(priorityId);
            logger.log(`🎯 PRIORITY LOADING: Set priority listing ID - ${priorityId}`);
          }
          setSelectedListing(modalStateFromURL.data);
          setIsModalOpen(true);
          setModalImageLoaded(false); // Reset modal image loaded state
          logger.log(`🔗 Deep link modal opened: ${modalStateFromURL.title}`);

          // Ensure grid scrolls into view under modal for deep links
          requestAnimationFrame(() => {
            const el = gridRef?.current;
            if (el) {
              try {
                const rect = el.getBoundingClientRect();
                const absoluteTop = rect.top + window.scrollY;
                const offsetBelowModal = window.innerHeight * 0.67;
                const targetTop = Math.max(0, absoluteTop - offsetBelowModal - 8);
                window.scrollTo({ top: targetTop, behavior: 'smooth' });
                el.scrollTop = 0;
              } catch (_) {}
            }
          });
        }
      }
    };

    loadData();
  }, []); // Only run once when component mounts

  // Handle browser history and modal routing
  useEffect(() => {
    const handleNavigationChange = (page, modalState) => {
      if (DEBUG_MODAL_FEED) console.log('🚨 MODAL NAVIGATION DETECTED:', modalState ? 'OPENING' : 'CLOSING');

      if (modalState) {
        if (DEBUG_MODAL_FEED) console.log('🚨 STEP 1: Modal opening, clearing all filters and settings');

        const incomingId = modalState.data?.etsyListingId || modalState.data?.listingId || null;
        const alreadyOpenSame = isModalOpen && lastModalListingIdRef.current === incomingId;
        if (alreadyOpenSame) {
          // Prevent redundant state updates if same modal already open
          return;
        }

        // Capture current scroll position to restore on close
        preModalScrollYRef.current = window.scrollY || 0;
        
        // Save current filter state from refs (not closure variables)
        preModalFilterTextRef.current = currentFilterTextRef.current;
        preModalSelectedTagsRef.current = [...currentSelectedTagsRef.current];
        
        console.log('💾 SAVED FILTERS BEFORE MODAL OPEN:', {
          fromState: filterText,
          fromRef: currentFilterTextRef.current,
          tags: selectedTags,
          tagsRef: currentSelectedTagsRef.current,
          savedInPreModal: preModalFilterTextRef.current
        });

        // Modal should be open with the provided listing data
        setSelectedListing(modalState.data);
        setIsModalOpen(true);
        setModalImageLoaded(false); // Reset modal image loaded state
        lastModalListingIdRef.current = incomingId;
        didScrollForModalRef.current = false;

        // STEP 1: Remove all default settings - clear search, filters, grouping
        setFilterText('');
        setSelectedTags([]);
        setGroupByCategory(false);

        if (DEBUG_MODAL_FEED) console.log('🚨 STEP 1 COMPLETE: Cleared search, category, tags, grouping');

        // Resort feed for modal context (category-first + title similarity)
        // Defer to next frame to ensure listing state has settled
        // Run after a microtask + next frame to avoid race with any pending setState
        Promise.resolve().then(() => {
          requestAnimationFrame(() => resortFeedForSimilarity(modalState.data));
        });

        // Scroll page so grid's first row appears just below the modal overlay (67vh)
        // 🎯 ALWAYS scroll and lock when modal opens (not just first time)
        requestAnimationFrame(() => {
          const el = gridRef?.current;
          if (el) {
            try {
              const rect = el.getBoundingClientRect();
              const absoluteTop = rect.top + window.scrollY;
              const offsetBelowModal = GRID_SCROLL_OFFSET; // From uiConstants: MODAL_MAX_HEIGHT (610px) + MODAL_SPACING (50px)
              const targetTop = Math.max(0, absoluteTop - offsetBelowModal);

              // Only scroll if we haven't scrolled for this modal yet
              if (!didScrollForModalRef.current) {
                window.scrollTo({ top: targetTop, behavior: 'smooth' });
                el.scrollTop = 0; // ensure internal grid scroll is at top
                didScrollForModalRef.current = true;
              }

              // ALWAYS install scroll lock (even when navigating between modals)
              minScrollLockRef.current = targetTop;

              // Install a passive scroll handler that clamps upward scrolling
              const clampScroll = () => {
                const minY = minScrollLockRef.current;
                if (minY != null && window.scrollY < minY - 1) {
                  window.scrollTo(0, minY);
                }
              };

              // Remove any previous handler before adding
              if (scrollLockHandlerRef.current) {
                window.removeEventListener('scroll', scrollLockHandlerRef.current, { passive: true });
              }
              scrollLockHandlerRef.current = clampScroll;
              window.addEventListener('scroll', clampScroll, { passive: true });
            } catch (_) {}
          }
        });

      } else {
        // Modal should be closed
        setIsModalOpen(false);
        setSelectedListing(null);
        lastModalListingIdRef.current = null;
        didScrollForModalRef.current = false;
        
        // Check if filters were active before modal opened
        console.log('🔍 CHECKING SAVED FILTERS ON MODAL CLOSE:', {
          savedFilterText: preModalFilterTextRef.current,
          savedTags: preModalSelectedTagsRef.current,
          currentFilterText: filterText,
          currentTags: selectedTags
        });
        
        const hadActiveFilters = preModalFilterTextRef.current.trim() || preModalSelectedTagsRef.current.length > 0;
        
        console.log('🔍 hadActiveFilters:', hadActiveFilters);
        
        if (hadActiveFilters) {
          // Filters were active - restore them and manually re-filter the data
          logger.log('🔍 Restoring search filters after modal close:', {
            filterText: preModalFilterTextRef.current,
            tags: preModalSelectedTagsRef.current.length
          });
          
          // Restore the grouping preference
          setGroupByCategory(userGroupByCategory);
          
          // Manually filter the listings based on saved filters
          const savedFilterText = preModalFilterTextRef.current;
          const savedTags = preModalSelectedTagsRef.current;
          
          let filtered = [...allListings];
          
          // Apply text filter
          if (savedFilterText.length >= 2) {
            const searchTerm = savedFilterText.toLowerCase();
            filtered = filtered.filter(listing => {
              const title = listing.title.toLowerCase();
              const category = listing.category?.toLowerCase() || '';
              
              let tagsMatch = false;
              if (listing.tags && Array.isArray(listing.tags)) {
                tagsMatch = listing.tags.some(tag => tag.toLowerCase().includes(searchTerm));
              }
              
              let skusMatch = false;
              if (listing.listing_data) {
                try {
                  const listingData = JSON.parse(listing.listing_data);
                  if (listingData.skus && Array.isArray(listingData.skus)) {
                    skusMatch = listingData.skus.some(sku => sku.toLowerCase().includes(searchTerm));
                  }
                } catch (error) {}
              }
              
              // Search: title, category, tags, SKUs (NOT description to avoid generic matches)
              return title.includes(searchTerm) || 
                     category.includes(searchTerm) ||
                     tagsMatch ||
                     skusMatch ||
                     title.split(' ').some(word => word.includes(searchTerm)) ||
                     title.split('-').some(word => word.includes(searchTerm));
            });
          }
          
          // Apply tag filter
          if (savedTags.length > 0) {
            filtered = filtered.filter(listing => {
              if (!listing.tags || !Array.isArray(listing.tags) || listing.tags.length === 0) return false;
              const listingTagNames = listing.tags.map(tag => tag.toLowerCase());
              return savedTags.some(selectedTag => 
                listingTagNames.includes(selectedTag.toLowerCase())
              );
            });
          }
          
          // Apply the filtered results
          setListings(filtered);
          
          // Restore the filter state to the inputs
          setFilterText(savedFilterText);
          setSelectedTags(savedTags);
          
          // Clear the saved refs
          preModalFilterTextRef.current = '';
          preModalSelectedTagsRef.current = [];
        } else {
          // No filters were active, restore original feed sorting
          restoreOriginalFeedSorting();
        }

        // Restore scroll position captured before modal opened
        requestAnimationFrame(() => {
          const y = Math.max(0, preModalScrollYRef.current || 0);
          try {
            window.scrollTo({ top: y, behavior: 'auto' });
          } catch (_) {
            window.scrollTo(0, y);
          }
          preModalScrollYRef.current = 0;
        });

        // Remove scroll clamp
        if (scrollLockHandlerRef.current) {
          window.removeEventListener('scroll', scrollLockHandlerRef.current, { passive: true });
          scrollLockHandlerRef.current = null;
        }
        minScrollLockRef.current = null;
      }
    };

    const handlePopState = (event) => {
      navigationService.handlePopState(event);
    };

    // Add navigation listener
    const unsubscribe = navigationService.addListener(handleNavigationChange);

    // Add browser history listener
    window.addEventListener('popstate', handlePopState);

    return () => {
      unsubscribe();
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Listen for background data refresh events
  useEffect(() => {
    const unsubscribe = intelligentLoadingService.on('dataRefreshed', (event) => {
      if (event.type === 'listings' && event.data) {
        if (isModalOpen) {
          logger.log('⏭️ Skipping background refresh UI update while modal is open');
          return;
        }
        logger.log('🔄 Background refresh completed, updating UI');
        setAllListings(event.data);
        setListings(event.data);
      }
    });
    
    return unsubscribe;
  }, [isModalOpen]);
  
  
  


  // Handle sort changes with client-side sorting (no API calls needed)
  useEffect(() => {
    // Skip if this is the initial load or no data loaded yet
    if (allListings.length === 0) return;
    

    
    // Sort the existing data client-side for instant response
    const sortedListings = [...allListings].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'sales':
          aValue = parseInt(a.total_sales_volume) || 0;
          bValue = parseInt(b.total_sales_volume) || 0;
          break;
        case 'views':
          aValue = parseInt(a.views) || 0;
          bValue = parseInt(b.views) || 0;
          break;
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'date':
          aValue = new Date(a.last_etsy_update || a.updated_at || a.created_at || 0);
          bValue = new Date(b.last_etsy_update || b.updated_at || b.created_at || 0);
          break;
        case 'isTrueScale':
          aValue = a.isTrueScale ? 1 : 0;
          bValue = b.isTrueScale ? 1 : 0;
          break;
        default:
          return 0;
      }
      
      // Handle string vs number comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        if (sortOrder === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else {
        if (sortOrder === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }
    });
    

    setAllListings(sortedListings);
    // Note: setListings will be updated by the filtering effect
  }, [sortBy, sortOrder]); // Only trigger on sort changes

  // Local filtering effect (uses client-side sorted data)
  useEffect(() => {
    // Skip normal filtering when modal is open - let modal control the sorting
    if (isModalOpen) {
      return;
    }

    // If no filters are applied, use the sorted allListings directly
    if ((filterText.length === 0 || filterText.length < 2) && selectedTags.length === 0) {
      setListings(allListings);
      return;
    }
    
    // Only apply filtering when there are actual filters
    let processedListings = [...allListings];
    
    // Apply text filtering
    if (filterText.length >= 2) {
      const searchTerm = filterText.toLowerCase();
      processedListings = processedListings.filter(listing => {
        const title = listing.title.toLowerCase();
        const category = listing.category?.toLowerCase() || '';
        
        // Check tags
        let tagsMatch = false;
        if (listing.tags && Array.isArray(listing.tags)) {
          tagsMatch = listing.tags.some(tag => tag.toLowerCase().includes(searchTerm));
        }
        
        // Check SKUs (from listing_data if available)
        let skusMatch = false;
        if (listing.listing_data) {
          try {
            const listingData = JSON.parse(listing.listing_data);
            if (listingData.skus && Array.isArray(listingData.skus)) {
              skusMatch = listingData.skus.some(sku => sku.toLowerCase().includes(searchTerm));
            }
          } catch (error) {
            logger.warn('Error parsing listing_data for SKU search:', error);
          }
        }
        
        // Search: title, category, tags, SKUs (NOT description to avoid generic matches)
        return title.includes(searchTerm) || 
               category.includes(searchTerm) ||
               tagsMatch ||
               skusMatch ||
               title.split(' ').some(word => word.includes(searchTerm)) ||
               title.split('-').some(word => word.includes(searchTerm));
      });
    }
    
    // Apply tag filtering (OR logic - match any selected tag)
    if (selectedTags.length > 0) {
      processedListings = processedListings.filter(listing => {
        if (!listing.tags || !Array.isArray(listing.tags) || listing.tags.length === 0) return false;
        
        const listingTagNames = listing.tags.map(tag => tag.toLowerCase());
        return selectedTags.some(selectedTag => 
          listingTagNames.includes(selectedTag.toLowerCase())
        );
      });
    }
    
    // Apply grid reverse if enabled
    if (isGridReversed) {
      processedListings.reverse();
    }
    
    setListings(processedListings);
  }, [filterText, allListings, sortBy, sortOrder, selectedTags, isGridReversed, isModalOpen]);





  // Handle search input
  const handleSearch = (e) => {
    const value = e.target.value;
    setFilterText(value);
    
    // Track search for analytics
    if (value.trim()) {
      trackSearchFilter('text_search', value.trim(), listings.length);
    }
  };

  // Handle sort change
  const handleSort = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newSortBy = e.target.value;
    setSortBy(newSortBy);
    
    // Track sort change for analytics
    trackSearchFilter('sort', newSortBy, listings.length);
    
    // Set appropriate default sort order for each type
    if (newSortBy === 'title') {
      setSortOrder('asc'); // A-Z for titles
    } else if (newSortBy === 'sales' || newSortBy === 'views') {
      setSortOrder('desc'); // High to low for sales/views
    } else if (newSortBy === 'date') {
      setSortOrder('desc'); // Newest first for dates
    }
  };

  // Handle sort order toggle
  const handleSortOrder = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSortOrder(prev => {
      const newSortOrder = prev === 'asc' ? 'desc' : 'asc';
      
      // Track sort order change for analytics
      trackSearchFilter('sort_order', newSortOrder, listings.length);
      
      return newSortOrder;
    });
  };


  // Handle grid reverse from tag filter
  const handleGridReverseChange = useCallback((isReversed) => {
    setIsGridReversed(isReversed);
  }, []);

  // Handle add to scene (placeholder for now)
  const handleAddToScene = useCallback((stickerData) => {

    // TODO: Implement add to scene functionality
  }, []);

  // Handle card click
  const handleCardClick = useCallback((listing) => {
    // DEBUG: Log when modal is opened
    logger.log('🖱️ Modal opened for listing:', {
      listingId: listing.etsyListingId || listing.listingId,
      title: listing.title,
      stackTrace: new Error().stack
    });
    
    // Track card click for analytics
    trackStickerEngagement(
      listing.etsyListingId || listing.listingId, 
      'card_click', 
      listing.category, 
      null,
      listing.title
    );

    // Use navigation service to open modal with URL routing
    navigationService.openModal(listing);
  }, []);

  // Handle modal close
  // Handle modal image load - resume grid image loading after modal image loads
  const handleModalImageLoad = useCallback(() => {
    setModalImageLoaded(true);
    logger.log('✅ Modal image loaded - resuming grid image loading');
  }, []);

  const handleModalClose = useCallback(() => {
    // Track modal close for analytics
    if (selectedListing) {
      trackStickerEngagement(
        selectedListing.etsyListingId || selectedListing.listingId,
        'modal_close',
        selectedListing.category,
        null,
        selectedListing.title
      );
    }

    // Reset modal image loaded state so grid can resume loading
    setModalImageLoaded(false);
    logger.log('🔄 Modal closed - resetting image load state for next modal');

    // Use navigation service to close modal with URL routing
    navigationService.closeModal();
  }, [selectedListing]);

  // Store original feed order for restoration
  const [originalFeedOrder, setOriginalFeedOrder] = useState([]);
  const originalFeedOrderRef = useRef([]);

  // Restore original feed sorting
  const restoreOriginalFeedSorting = useCallback(() => {
    if (originalFeedOrder.length > 0) {
      setListings([...originalFeedOrder]);
      setOriginalFeedOrder([]); // Clear stored order
      originalFeedOrderRef.current = [];

      // Restore category grouping based on user preference
      setGroupByCategory(userGroupByCategory);

      logger.log('🔄 Feed restored to original sorting');
      logger.log('🔄 Restored category grouping setting');
    }
  }, [originalFeedOrder, userGroupByCategory]);

  // Simple title similarity (fraction of common words)
  const calculateTitleSimilarity = useCallback((title1, title2) => {
    if (!title1 || !title2) return 0;
    const words1 = String(title1).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    const words2 = new Set(String(title2).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));
    if (words1.length === 0) return 0;
    let common = 0;
    for (const w of words1) {
      if (words2.has(w)) common++;
    }
    return common / words1.length;
  }, []);

  // Category-first filter, then sort by title similarity
  const resortFeedForSimilarity = useCallback((selectedSticker) => {
    try {
      if (!selectedSticker || allListings.length === 0) return;

      // Store original order once
      if (originalFeedOrderRef.current.length === 0) {
        const base = listings.length ? listings : allListings;
        setOriginalFeedOrder(base);
        originalFeedOrderRef.current = base;
      }

      // Force simple grid
      setGroupByCategory(false);

      // Normalize category to handle variations like 'Dinosaur', 'Dinosaurs', 'Dino', 'Pop Culture Stickers', etc.
      const normalizeCategory = (cat) => {
        let s = String(cat || '').toLowerCase();
        // remove generic suffixes/words
        s = s.replace(/stickers?|collection|decals?|vinyl/g, '');
        s = s.replace(/\s+/g, ' ').trim();
        // unify dinosaur tokens
        if (s.includes('dino')) s = 'dinosaur';
        if (s.includes('pop culture')) s = 'pop culture';
        return s;
      };
      const categoriesMatch = (a, b) => normalizeCategory(a) === normalizeCategory(b);
      const selectedCategory = normalizeCategory(selectedSticker.category);
      const selectedId = selectedSticker.etsyListingId || selectedSticker.listingId;

      // Build normalized tag sets and holiday weighting
      const normalizeTag = (t) => String(t || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      const canonicalizeTag = (t) => {
        let s = normalizeTag(t);
        if (s === 'x-mas' || s === 'x mas' || s === 'xmas') s = 'christmas';
        if (s.endsWith('s') && !['xmas', 'christmas'].includes(s)) s = s.slice(0, -1); // holidays -> holiday
        return s;
      };
      const HOLIDAY_KEYWORDS = new Set([
        'holiday', 'christmas', 'winter', 'snow', 'santa', 'rudolph', 'reindeer',
        'elf', 'elves', 'ornament', 'tree', 'lights', 'present', 'gift',
        'wreath', 'stocking', 'north pole', 'grinch', 'bumble', 'abominable',
        'snowman', 'mistletoe', 'yule', 'hanukkah', 'kwanzaa'
      ].map(canonicalizeTag));

      const getNormalizedTags = (listing) => {
        const tags = Array.isArray(listing?.tags) ? listing.tags : [];
        return new Set(tags.map(canonicalizeTag).filter(Boolean));
      };
      // Also derive holiday-ish keywords from title for extra recall
      const getHolidayHintsFromTitle = (title) => {
        const words = String(title || '')
          .toLowerCase()
          .split(/[^a-z0-9]+/)
          .filter(Boolean)
          .map(canonicalizeTag);
        return new Set(words.filter(w => HOLIDAY_KEYWORDS.has(w)));
      };

      const selectedTags = getNormalizedTags(selectedSticker);
      // Merge in holiday hints from title
      for (const w of getHolidayHintsFromTitle(selectedSticker.title)) selectedTags.add(w);

      // Resort FULL grid: same-category items first (not filtering others),
      // then title similarity, then tag overlap (holiday keywords weighted);
      // keep others (non-matching category) in original order. No svgPath influence.
      const ranked = allListings
        .filter(item => (item.etsyListingId !== selectedId)) // avoid duplicating selected card
        .map((item, index) => {
          const sameCategory = categoriesMatch(item.category, selectedCategory);
          const itemTags = getNormalizedTags(item);
          // Tag overlap score with weighting for holiday terms
          let tagScore = 0;
          if (sameCategory && itemTags.size > 0 && selectedTags.size > 0) {
            for (const tag of itemTags) {
              if (selectedTags.has(tag)) {
                tagScore += HOLIDAY_KEYWORDS.has(tag) ? 5 : 1;
              }
            }
          }
          return {
            item,
            index, // to preserve stable order for non-matching category
            sameCategory,
            tagScore,
            titleScore: sameCategory ? calculateTitleSimilarity(selectedSticker.title, item.title) : 0
          };
        })
        .sort((a, b) => {
          // Category priority
          if (a.sameCategory !== b.sameCategory) return a.sameCategory ? -1 : 1;
          // Within same category, prefer title similarity first, then tag overlap (holiday weighted)
          if (a.sameCategory && b.sameCategory) {
            if (b.titleScore !== a.titleScore) return b.titleScore - a.titleScore;
            if (b.tagScore !== a.tagScore) return b.tagScore - a.tagScore;
          }
          // Fallback to original order to avoid reordering the rest
          return a.index - b.index;
        })
        .map(x => x.item);

      setListings(ranked);
      if (DEBUG_MODAL_FEED) console.log(`🔄 Modal feed resorted: ${ranked.length} items; '${selectedSticker.category}' first`);
    } catch (err) {
      console.warn('resortFeedForSimilarity failed:', err);
    }
  }, [allListings, calculateTitleSimilarity]);

  // When modal opens with a selected listing, enforce modal feed filtering
  useEffect(() => {
    if (isModalOpen && selectedListing && allListings.length > 0) {
      if (DEBUG_MODAL_FEED) console.log('🎯 Enforcing modal resort for:', selectedListing.title, selectedListing.category);
      resortFeedForSimilarity(selectedListing);
    }
  }, [isModalOpen, selectedListing, allListings.length, resortFeedForSimilarity]);

  // Calculate similarity score between two stickers (same logic as SimilarStickers)
  const calculateSimilarityScore = useCallback((source, target) => {
    let score = 0;

    // Category match (highest weight - 40 points)
    if (source.category && target.category && source.category.toLowerCase() === target.category.toLowerCase()) {
      score += 40;
    }

    // Extract and compare tags (35 points max)
    const sourceTags = extractTags(source.title, source.description);
    const targetTags = extractTags(target.title, target.description);
    const commonTags = sourceTags.filter(tag => targetTags.includes(tag));

    if (commonTags.length > 0) {
      // Weight more heavily for important theme matches
      const themeWords = ['dinosaur', 'superhero', 'horror', 'anime', 'gaming', 'space', 'fantasy'];
      const themeMatches = commonTags.filter(tag => themeWords.includes(tag));

      score += Math.min(commonTags.length * 7, 35); // 7 points per matching tag, max 35
      score += themeMatches.length * 10; // Extra 10 points for theme matches
    }

    // Description keyword matching (20 points max)
    if (source.description && target.description) {
      const sourceWords = source.description.toLowerCase()
        .split(/[\s,.-]+/)
        .filter(word => word.length > 3 && !['that', 'this', 'with', 'from', 'they', 'them', 'your', 'were', 'been', 'have'].includes(word));
      const targetWords = target.description.toLowerCase()
        .split(/[\s,.-]+/)
        .filter(word => word.length > 3 && !['that', 'this', 'with', 'from', 'they', 'them', 'your', 'were', 'been', 'have'].includes(word));
      const commonWords = sourceWords.filter(word => targetWords.includes(word));
      score += Math.min(commonWords.length * 3, 20);
    }

    // Title keyword matching (15 points max)
    if (source.title && target.title) {
      const sourceTitleWords = source.title.toLowerCase()
        .split(/[\s,.-]+/)
        .filter(word => word.length > 3);
      const targetTitleWords = target.title.toLowerCase()
        .split(/[\s,.-]+/)
        .filter(word => word.length > 3);
      const commonTitleWords = sourceTitleWords.filter(word => targetTitleWords.includes(word));
      score += Math.min(commonTitleWords.length * 5, 15);
    }

    return score;
  }, []);

  // Extract meaningful tags from title and description (same logic as SimilarStickers)
  const extractTags = useCallback((title, description) => {
    const text = `${title || ''} ${description || ''}`.toLowerCase();

    // ONLY thematic keywords that indicate actual content similarity
    const thematicKeywords = [
      // Dinosaur/Prehistoric themes
      'dinosaur', 'dino', 'jurassic', 'raptor', 'rex', 'stegosaurus', 'triceratops', 'pterodactyl',
      'prehistoric', 'fossil', 'paleontology', 'mesozoic',

      // Superhero themes
      'superhero', 'batman', 'superman', 'spiderman', 'wonder woman', 'aquaman', 'flash',
      'marvel', 'dc', 'comics', 'avengers', 'justice league', 'x-men', 'hulk', 'thor',
      'captain america', 'iron man', 'deadpool', 'wolverine',

      // Star Wars universe
      'star wars', 'jedi', 'sith', 'empire', 'rebel', 'vader', 'luke', 'leia',
      'yoda', 'chewbacca', 'han solo', 'millennium falcon', 'death star',

      // Horror themes
      'horror', 'scary', 'zombie', 'skull', 'gothic', 'vampire', 'werewolf',
      'ghost', 'demon', 'monster', 'creepy', 'spooky', 'haunted',

      // Anime/Manga themes
      'anime', 'manga', 'kawaii', 'chibi', 'otaku', 'naruto', 'dragon ball',
      'pokemon', 'sailor moon', 'one piece', 'attack on titan',

      // Gaming themes
      'gaming', 'retro', 'arcade', 'pixel', '8bit', 'nintendo', 'mario',
      'zelda', 'sonic', 'pac-man', 'tetris', 'minecraft', 'fortnite',

      // Music themes
      'music', 'band', 'rock', 'metal', 'punk', 'classical', 'jazz',
      'guitar', 'drums', 'piano', 'violin', 'concert',

      // Animal themes (specific animals, not generic)
      'cat', 'kitten', 'dog', 'puppy', 'wolf', 'lion', 'tiger', 'bear',
      'elephant', 'giraffe', 'zebra', 'monkey', 'bird', 'eagle', 'owl',
      'fish', 'shark', 'whale', 'dolphin', 'octopus',

      // Space themes
      'space', 'galaxy', 'planet', 'astronaut', 'rocket', 'alien', 'ufo',
      'mars', 'moon', 'solar system', 'spacecraft', 'nasa',

      // Fantasy themes
      'fantasy', 'dragon', 'unicorn', 'fairy', 'magic', 'wizard', 'witch',
      'princess', 'knight', 'castle', 'medieval', 'elf', 'dwarf',

      // Sports themes
      'football', 'basketball', 'baseball', 'soccer', 'hockey', 'tennis',
      'golf', 'swimming', 'running', 'cycling', 'skateboard',

      // Vehicle themes (specific types)
      'motorcycle', 'truck', 'jeep', 'corvette', 'mustang', 'camaro',
      'airplane', 'helicopter', 'boat', 'submarine', 'train',

      // Pop culture characters/franchises
      'mickey mouse', 'disney', 'pixar', 'minions', 'shrek', 'frozen',
      'harry potter', 'lord of the rings', 'game of thrones',

      // Nature themes (specific)
      'ocean', 'mountain', 'forest', 'desert', 'beach', 'sunset',
      'tree', 'flower', 'rose', 'sunflower', 'butterfly', 'bee'
    ];

    // Explicitly exclude common product attributes that don't indicate similarity
    const excludedCommonTerms = [
      'vinyl', 'decal', 'sticker', 'bumper', 'windshield', 'window', 'car',
      'easter', 'egg', 'micro', 'nano', 'mini', 'small', 'medium', 'large',
      'pack', 'family', 'truescale', 'scale', 'collection', 'series', 'set',
      'quality', 'fade', 'resistant', 'adhesive', 'shipping', 'usps',
      'business', 'days', 'tracking', 'replacement', 'order', 'address',
      'size', 'color', 'variation', 'option', 'price', 'available'
    ];

    return thematicKeywords.filter(keyword => {
      return text.includes(keyword) && !excludedCommonTerms.some(excluded => keyword.includes(excluded));
    });
  }, []);

  // Loading state with progress
  if (loading) {
    return (
      <div className="loading-container">
        <ClothedEyeLoader 
          size="large" 
          showTagline={true}
          fullscreen={true}
          message={loadingProgress.message || "Loading stickers..."}
        />
      </div>
    );
  }

  // Duplicate loading state removed - using the main one above with progress

  // Error state
  if (error) {
    return (
      <ClothedEyeError
        errorType="network"
        title="Failed to Load Stickers"
        description={error || "We couldn't load the stickers. Please check your internet connection and try again."}
        onRetry={() => window.location.reload()}
        onGoHome={() => window.location.href = '/'}
        fullscreen={true}
        size="medium"
        theme="dark"
      />
    );
  }

  return (
    <div className={`new-homepage ${isModalOpen ? 'modal-open' : ''}`}>
      {/* Global Header with Navigation */}
      <MainHeader currentPage="home" onVisibilityChange={setHeaderVisible} />

      {/* Hero Scene */}
              <HeroScene 
          onStickerClick={handleStickerClick} 
          allStickers={allListings}
          isLoadingStickers={loading}
          useSmartLoading={true}
        />

      {/* Controls Section */}
      <section className="controls-section">
        <div className="controls-container">
          {/* Compact Navigation - Shown when header scrolls out of view */}
          <div className={`compact-navigation ${!headerVisible ? 'visible' : 'hidden'}`}>
            <MainNavigation currentPage="home" compact={true} />
          </div>
          
          {/* Controls Group - Slides with navigation */}
          <div className="controls-group">
            {/* Search - Consistent Design */}
            <div className="search-container">
            <div className="search-field">
              <MaterialIcon icon="search" size={20} className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search stickers..."
                onChange={handleSearch}
                className="search-input"
                id="search-input"
                key="search-input-stable"
              />
              {filterText && (
                <button
                  type="button"
                  onClick={() => {
                    setFilterText('');
                    if (searchInputRef.current) {
                      searchInputRef.current.value = '';
                    }
                  }}
                  className="search-clear-btn"
                  title="Clear search"
                  aria-label="Clear search"
                >
                  <MaterialIcon icon="close" size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Tag Filter */}
          <TagFilter
            allTags={allTags}
            selectedTags={selectedTags}
            onTagsChange={(newSelectedTags) => {
              setSelectedTags(newSelectedTags);
              
              // Track tag filter changes for analytics
              if (newSelectedTags.length > selectedTags.length) {
                // Tag was added
                const addedTag = newSelectedTags.find(tag => !selectedTags.includes(tag));
                if (addedTag) {
                  trackSearchFilter('tag_add', addedTag, listings.length);
                }
              } else if (newSelectedTags.length > 0) {
                // Tags were changed but not through removal (which is tracked elsewhere)
                trackSearchFilter('tag_filter', newSelectedTags.join(', '), listings.length);
              }
            }}
            onGridReverseChange={handleGridReverseChange}
            maxTags={10}
          />

          {/* Sort Controls */}
          <div className="sort-container" ref={sortDropdownRef}>
            <button className="unified-control-button" onClick={() => setShowSortDropdown(!showSortDropdown)}>
              <MaterialIcon 
                icon="sort" 
                size={20} 
                onClick={(e) => {
                  e.stopPropagation();
                  const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
                  setSortOrder(newOrder);
                  trackSearchFilter('sort_reverse', `${sortBy}_${newOrder}`, listings.length);
                }}
                className={`sort-icon ${sortOrder === 'asc' ? 'reversed' : ''}`}
                title={sortOrder === 'desc' ? 'Click to sort ascending' : 'Click to sort descending'}
              />
              <span className="sort-text">
                {sortBy === 'sales' ? 'Sort by Sales' :
                 sortBy === 'title' ? 'Sort by Title' :
                 sortBy === 'views' ? 'Sort by Views' :
                 sortBy === 'isTrueScale' ? 'Sort by TRUEscale' :
                 'Sort by Date Updated'}
              </span>
              <MaterialIcon icon={showSortDropdown ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} size={20} />
            </button>
            {showSortDropdown && (
              <div className="sort-dropdown">
                <button onClick={() => { setSortBy('sales'); setSortOrder('desc'); setShowSortDropdown(false); trackSearchFilter('sort', 'sales', listings.length); }} className={`sort-option ${sortBy === 'sales' ? 'selected' : ''}`}>
                  Sort by Sales
                </button>
                <button onClick={() => { setSortBy('title'); setSortOrder('asc'); setShowSortDropdown(false); trackSearchFilter('sort', 'title', listings.length); }} className={`sort-option ${sortBy === 'title' ? 'selected' : ''}`}>
                  Sort by Title
                </button>
                <button onClick={() => { setSortBy('views'); setSortOrder('desc'); setShowSortDropdown(false); trackSearchFilter('sort', 'views', listings.length); }} className={`sort-option ${sortBy === 'views' ? 'selected' : ''}`}>
                  Sort by Views
                </button>
                <button onClick={() => { setSortBy('date'); setSortOrder('desc'); setShowSortDropdown(false); trackSearchFilter('sort', 'date', listings.length); }} className={`sort-option ${sortBy === 'date' ? 'selected' : ''}`}>
                  Sort by Date Updated
                </button>
                <button onClick={() => { setSortBy('isTrueScale'); setSortOrder('desc'); setShowSortDropdown(false); trackSearchFilter('sort', 'truescale', listings.length); }} className={`sort-option ${sortBy === 'isTrueScale' ? 'selected' : ''}`}>
                  Sort by TRUEscale
                </button>
              </div>
            )}
          </div>

          {/* Group by Category Toggle */}
          <div className="group-container">
            <button 
              onClick={() => {
                const newValue = !userGroupByCategory;
                setUserGroupByCategory(newValue);
                
                // Track group by category toggle for analytics
                trackSearchFilter('group_by_category', newValue ? 'enabled' : 'disabled', listings.length);
                
                // If no filters active, apply immediately
                if (filterText.trim() === '' && selectedTags.length === 0) {
                  setGroupByCategory(newValue);
                }
              }}
              className={`unified-control-button group-toggle-btn ${groupByCategory ? 'active' : ''}`}
              title={filterText.trim() !== '' || selectedTags.length > 0 ? 'Grouping disabled when filters are active' : ''}
            >
              <MaterialIcon icon={groupByCategory ? 'view_list' : 'view_module'} size={20} />
              {groupByCategory ? 'Ungroup' : 'Group by Section'}
            </button>
          </div>
          </div> {/* End controls-group */}
        </div>

        {/* Selected Tags Display */}
        {selectedTags.length > 0 && (
          <div className="selected-tags-display">
            {selectedTags.map(tag => (
              <TagChip
                key={tag}
                tag={tag}
                onRemove={() => {
                  setSelectedTags(prev => {
                    const newTags = prev.filter(t => t !== tag);
                    // Track tag removal for analytics
                    trackSearchFilter('tag_remove', tag, listings.length);
                    return newTags;
                  });
                }}
              />
            ))}
          </div>
        )}

        {/* Results Counter - Only show while loading */}
        {loading && (
          <div className="results-counter">
            {groupByCategory ? (
              <>
                {listings.length} loaded stickers
                {filterText && ` matching "${filterText}"`}
                {allListings.length > listings.length && ` of ${allListings.length} total loaded`}
              </>
            ) : (
              <>
                Showing {listings.length} of {allListings.length} loaded stickers
                {filterText && ` matching "${filterText}"`}
                {loading && ' • Loading more...'}
              </>
            )}
          </div>
        )}

      </section>

      {/* Stickers Grid */}
      <StickerGrid
        listings={listings}
        loading={loading}
        error={error}
        groupByCategory={groupByCategory}
        isModalOpen={isModalOpen}
        modalImageLoaded={modalImageLoaded}
        containerRef={gridRef}
        onCardClick={handleCardClick}
        onAddToScene={handleAddToScene}
        showCategoryHeaders={true}
        categorySceneAssignments={categorySceneAssignments}
        onStickerClick={handleStickerClick}
        onSceneStickersUpdate={handleHomeSceneStickersUpdate}
        onSceneNameUpdate={handleHomeSceneNameUpdate}
        onAdminToggle={handleAdminToggle}
        sortBy={sortBy}
        sortOrder={sortOrder}
        priorityListingId={priorityListingId}
      />

      {/* Footer */}
      <footer className="new-homepage-footer">
        <p>&copy; 2024 Clothed Eye. All rights reserved.</p>
      </footer>

      {/* Modal for sticker details */}
      <StickerModal
        isOpen={isModalOpen}
        selectedListing={selectedListing}
        onClose={handleModalClose}
        onAddToScene={handleAddToScene}
        isPriorityLoad={priorityListingId === selectedListing?.etsyListingId}
        onImageLoad={handleModalImageLoad}
      />

    </div>
  );
});

NewHomePage.propTypes = {
  isInitialAppLoad: PropTypes.bool,
  hasLoadedData: PropTypes.bool
};

export default NewHomePage; 